import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import clone from 'clone';
import qs from 'qs';
import {
  Grid,
  Loader,
  Divider,
  Form,
  Table,
  Segment,
  Button,
} from 'semantic-ui-react';
import { required, stringLength, identity as ValidateIdentity, matchRegex } from '@mhub/web-validations';
import { withTranslation } from 'react-i18next';
import { flashErrorMessage, flashMessage } from '../../actions/flash';
import { fetchM } from '../../actions/fetchM';
import config from '../../../config';
import STATES from '../../constants/states';
import { SELECT_COUNTRIES } from '../../constants/countries';
import RESERVATION_LIMIT from '../../constants/reservationLimit';
import phone from '../../utils/validations/phone';
import INTL_CALL_CODES from '../../constants/intlCallCodes';
import AppForm from '../../components/AppForm';
import ListItem from '../../components/ListItem';
import PhoneInput from '../../components/AppForm/PhoneInput';
import ProjectsList from '../modals/ProjectsList';
import BU_STATUS_FILTER from '../../constants/businessUnitStatusFilter';
import { isForeignCompany } from '../../utils/companies';
import INTL_FLAG_CODES from '../../constants/intlFlagCodes';
import TinFieldInput from '../../components/AppForm/TinFieldInput';
import { REGEX_TIN_VALID, REGEX_NPWP_ID } from '../../constants/regex';
import { getIdentityTypeOpts } from '../../utils/identityTypes';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import { translateModels } from '../../utils/translation';
import { GENERIC_TIN_UNMATCHED_ERROR_MSG, GENERIC_TIN_VALUES, getGenericTinByIdTypeAndTinNo, GOVERNMENT_GENERIC_TIN, isGenericTinFound } from '../../constants/tin';
import { IDENTITY_TYPE_BRN } from '../../constants/defaultIdentityTypeOpts';

class BusinessUnitForm extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    routeParams: PropTypes.shape({
      id: PropTypes.string,
    }),
    auth: PropTypes.shape({
      isBranchLeader: PropTypes.bool,
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    t: PropTypes.func,
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  static defaultBusinessUnit = {
    registrationNumber: '',
    taxIdentificationNo: '',
    taxTinValidationStatus: 'failed',
    name: '',
    shortName: '',
    email: '',
    phonePrefix: '60',
    phone: '',
    department: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    state: '',
    country: 'my',
    gps: '',
    viewAllProjects: true,
    viewArchivedProjects: true,
    viewDraftProjects: true,
    viewReadyProjects: true,
    branchProjects: [],
    reservationLimit: '0',
    statusID: 'active',
  }
  static defaultUser = {
    id: null,
    groups: [],
  }
  constructor(props) {
    super(props);
    const businessUnit = clone(BusinessUnitForm.defaultBusinessUnit);
    businessUnit.user = clone(BusinessUnitForm.defaultUser);
    const tinError = null;
    const auth = props.auth;

    if (auth && isForeignCompany(auth.company)) {
      const idx = INTL_FLAG_CODES.findIndex(f => f.countryCode === auth.company.countryCode);
      if (idx !== -1) {
        businessUnit.phonePrefix = INTL_FLAG_CODES[idx].value;
        businessUnit.country = INTL_FLAG_CODES[idx].countryCode;
      }
    }
    this.state = {
      businessUnit,
      errMessages: [],
      tinMessage: null,
      showAddProject: false,
      tinError,
      tinEnabled: true,
      tinRequired: false,
    };
    this.validation = {
      name: createRef(),
      shortName: createRef(),
      department: createRef(),
      taxIdentificationNo: createRef(),
      registrationNumber: createRef(),
      referenceCode: createRef(),
      email: createRef(),
      phonePrefix: createRef(),
      phone: createRef(),
      address1: createRef(),
      address2: createRef(),
      postcode: createRef(),
      city: createRef(),
      state: createRef(),
      country: createRef(),
    };
  }
  setLoading = (loading = false, loadingType) => new Promise((res) => {
    if (loadingType === 'validatingTIN') {
      this.setState({ validatingTin: loading }, res);
    }
  })
  submit(url, opts) {
    return this.form.submit(url, opts);
  }
  handleSubmit = (event) => {
    event.preventDefault();
    const { businessUnit } = this.state;
    return AppForm.validate(this.validation, businessUnit)
      .then((validations) => {
        this.setState({
          errMessages: validations.filter(v => !v.isValid),
        });

        businessUnit.taxTinValidationStatus = this.state.tinError === false ? 'success' : 'failed';

        if (this.state.errMessages.length === 0 && this.state.tinRequired && businessUnit.taxTinValidationStatus !== 'success') {
          const { store } = this.context;
          if (businessUnit.taxTinValidationStatus !== 'success') {
            store.dispatch(flashMessage({
              header: 'Invalid TIN',
              message: 'Tax Identification Number is invalid',
              icon: 'warning sign',
              type: 'error',
              autoHideDuration: 5000,
            }));
            return;
          }
        }
        this.props.onSubmit(validations, businessUnit);
      });
  }
  handleCheckChanges() {
    if (this.props.onChange) {
      this.props.onChange(true);
    }
  }
  handleInputChange = (event, data) => {
    const { businessUnit } = this.state;
    businessUnit[data.name] = data.value;
    this.setState({ businessUnit }, () => {
      // Validate TIN again on registrationNumber change.
      if (data.name === 'registrationNumber') {
        if (businessUnit.taxIdentificationNo !== '') {
          this.handleTinInput({ target: { value: businessUnit.taxIdentificationNo } });
        }
      }
      this.handleCheckChanges();
    });
  }
  async validateTIN(tinInput) {
    const { registrationNumber } = this.state.businessUnit;
    if (!tinInput || !registrationNumber) {
      return;
    }
    const idType = getIdentityTypeOpts().find(i => i.value === IDENTITY_TYPE_BRN).tin_code;
    const query = Object.assign({
      tin: tinInput,
      idType,
      idValue: registrationNumber,
    });
    const { store } = this.context;
    try {
      const res = await store.dispatch(fetchM(`/tin/validate/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
          'X-Origin': this.state.origin,
        },
      }));

      if (res && res.ok) {
        this.setState({
          tinError: false,
          tinMessage: null,
        });
      } else {
        const errorMessage = await res.text();
        this.setState({
          tinError: true,
          tinMessage: errorMessage,
        });
        return;
      }
    } catch (err) {
      if (err.res) {
        const errorMessage = await err.res.text();
        this.setState({
          tinError: true,
          tinMessage: errorMessage,
        });
      }
    }
  }
  handleTinInput = (e) => {
    const { auth } = this.props;
    const { businessUnit } = this.state;
    const isIndonesia = auth && auth.company && auth.company.countryCode.toUpperCase() === 'ID';

    if (isIndonesia) {
      this.state.businessUnit.taxIdentificationNo = e.target.value;
      this.setState({ taxIdentificationNo: e.target.value, businessUnit, tinError: false });
      return;
    }

    const tinInput = e.target.value;
    const isAlphanumeric = /^[a-zA-Z0-9]*$/.test(tinInput);

    if (tinInput === '') {
      this.state.businessUnit.taxIdentificationNo = '';
      this.setState({ taxIdentificationNo: '', businessUnit, tinError: false });
      return;
    }

    if (isAlphanumeric) {
      businessUnit.taxIdentificationNo = tinInput;
      this.setState({ businessUnit, tinError: true }, () => {
        this.handleCheckChanges();
      });
      // 1~2 letter followed by numeric with length 9 - 11
      const matched = REGEX_TIN_VALID.test(tinInput);
      const genericTin = getGenericTinByIdTypeAndTinNo(IDENTITY_TYPE_BRN, tinInput);
      this.setLoading(true, 'validatingTIN').then(() => {
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
        }

        this.debounceTimeout = setTimeout(() => {
          if (matched) {
            if (tinInput === GOVERNMENT_GENERIC_TIN) {
              this.setState({
                tinError: false,
                // tinFound: true,
                tinMessage: null },
              () => {
                this.setState({ validatingTin: false });
              });
            } else if (GENERIC_TIN_VALUES.includes(tinInput)) {
              this.setState({
                tinError: !isGenericTinFound(genericTin),
                // tinFound: isGenericTinFound(genericTin),
                tinMessage: !isGenericTinFound(genericTin) ? GENERIC_TIN_UNMATCHED_ERROR_MSG : null },
              () => {
                this.setState({ validatingTin: false });
              });
            } else {
              this.validateTIN(tinInput).finally(() => {
                this.setState({ validatingTin: false });
              });
            }
          } else {
            this.setState({ tinError: true }, () => {
              this.setState({ validatingTin: false });
            });
          }
        }, 3000);
      });
    }
  }
  handleCheckBoxChange = (event, d) => {
    const { businessUnit } = this.state;
    businessUnit[d.name] = !businessUnit[d.name];
    this.setState({ businessUnit });
  }
  handleRemoveProject = (index) => {
    if (this.state.edit) {
      const { businessUnit } = this.state;
      businessUnit.projects.splice(index, 1);
      this.setState({ businessUnit });
    }
  }
  handleAddProject = (e, projects) => {
    e.preventDefault();
    const { businessUnit } = this.state;
    businessUnit.projects = projects;
    this.setState({
      businessUnit,
      showAddProject: false,
    });
  }
  handleShowAddProject = (e) => {
    e.preventDefault();
    this.setState({
      showAddProject: true,
    });
  }
  handleCloseAddProject = (e) => {
    if (e) { e.preventDefault(); }
    this.setState({
      showAddProject: false,
    });
  }
  handleCheckBoxChange = (event, d) => {
    const { businessUnit } = this.state;
    businessUnit[d.name] = !businessUnit[d.name];
    if (d.name === 'isExternal') {
      businessUnit.viewArchivedProjects = !businessUnit.isExternal;
      businessUnit.viewDraftProjects = !businessUnit.isExternal;
      businessUnit.viewReadyProjects = !businessUnit.isExternal;
    }
    this.setState({ businessUnit });
  }
  handleOnCheck = (event, value) => {
    const { businessUnit } = this.state;
    businessUnit[value.name] = !businessUnit[value.name];
    if (value.name === 'viewAllProjects' && !businessUnit.viewAllProjects) {
      businessUnit.viewArchivedProjects = !businessUnit.isExternal;
      businessUnit.viewDraftProjects = !businessUnit.isExternal;
      businessUnit.viewReadyProjects = !businessUnit.isExternal;
    }
    this.setState({ businessUnit });
  }
  async fetchBusinessUnit() {
    await this.setLoading(true);
    const { store } = this.context;
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/company_branches/${this.props.routeParams.id}/`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: 'An error occurred, please refresh page' }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const token = res.headers.get('X-Csrf-Token');
      const data = await res.json();
      // handle token.. etc
      await this.setComponentState({
        token,
        data,
      });
      this.setState({
        oriData: clone(data),
      });
    }
    await this.setLoading(false);
  }
  render() {
    const { t, auth } = this.props;
    const { businessUnit } = this.state;
    const tinContent = businessUnit.taxIdentificationNo || this.state.tinRequired;
    const isIndonesia = auth && auth.company && auth.company.countryCode.toUpperCase() === 'ID';

    return (
      <AppForm
        onSubmit={this.handleSubmit}
        error={this.state.errMessages.length > 0}
        ref={(ref) => { this.form = ref; }}
      >
        <ProjectsList
          open={this.state.showAddProject}
          onClose={this.handleCloseAddProject}
          onConfirm={this.handleAddProject}
          onCheck={project => project.loanTrackerProjectID < 1}
          selected={businessUnit ? businessUnit.branchProjects : []}
        />
        <Grid columns="equal" stackable doubling>
          <Grid.Row>
            <Grid.Column>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={`${t('Business Unit Name')}*`}
                  validateLabel={t('Name')}
                  name="name"
                  value={businessUnit.name}
                  onChange={this.handleInputChange}
                  ref={this.validation.name}
                  validation={[required]}
                />
                <AppForm.Input
                  label={`${t('Business Unit Short Name')}*`}
                  validateLabel={t('Short name')}
                  name="shortName"
                  value={businessUnit.shortName}
                  onChange={this.handleInputChange}
                  ref={this.validation.shortName}
                  validation={[required]}
                />
              </Form.Group>
              <Form.Group widths="equal" style={{ alignItems: 'center' }}>
                <Form.Select
                  autoComplete="off"
                  label={t('Reservation Limit')}
                  fluid
                  name="reservationLimit"
                  options={RESERVATION_LIMIT.map(option => ({
                    ...option,
                    text: option.text === 'Unlimited' ? t('Unlimited', { ns: TRANSLATION_FILES.constants }) : option.text,
                  }))}
                  placeholder={translateModels(t, 'Select Limit', 'businessUnit')}
                  search
                  selectOnBlur={false}
                  value={businessUnit.reservationLimit}
                  onChange={this.handleInputChange}
                />
                <Form.Checkbox
                  label={t('Agency')}
                  name="isExternal"
                  checked={businessUnit.isExternal}
                  onChange={this.handleCheckBoxChange}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  label={translateModels(t, 'department', 'businessUnit')}
                  validateLabel={translateModels(t, 'department', 'businessUnit')}
                  name="department"
                  value={businessUnit.department}
                  onChange={this.handleInputChange}
                  ref={this.validation.department}
                />
                <Form.Field />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Field>
                  <AppForm.Input
                    label={translateModels(t, 'registrationNumber', 'businessUnit')}
                    validateLabel={translateModels(t, 'registrationNumber', 'businessUnit')}
                    name="registrationNumber"
                    required={tinContent}
                    validation={tinContent ?
                    [required,
                      {
                        validate: ValidateIdentity,
                        option: {
                          countryCode: 'MY',
                          identityType: 'other',
                        },
                      },
                    ] :
                    [{
                      validate: ValidateIdentity,
                      option: {
                        countryCode: 'MY',
                        identityType: 'other',
                      },
                    }]}
                    value={businessUnit.registrationNumber}
                    onChange={this.handleInputChange}
                    ref={this.validation.registrationNumber}
                  />
                </Form.Field>
                <Form.Field>
                  <TinFieldInput
                    isIndonesia={isIndonesia}
                    value={businessUnit.taxIdentificationNo}
                    errMessage={this.state.tinMessage}
                    isLoading={this.state.validatingTin}
                    tinError={!isIndonesia && this.state.tinError}
                    required={!isIndonesia && this.state.tinRequired}
                    disabled={!this.state.tinEnabled}
                    ref={this.validation.taxIdentificationNo}
                    onChange={this.handleTinInput}
                    validation={!isIndonesia && this.state.tinRequired ?
                    [required,
                      {
                        validate: matchRegex,
                        option: {
                          validRegex: REGEX_TIN_VALID,
                        },
                      },
                    ] :
                    [{
                      validate: matchRegex,
                      option: {
                        validRegex: isIndonesia ? REGEX_NPWP_ID : REGEX_TIN_VALID,
                      },
                    }]}
                    identityType={IDENTITY_TYPE_BRN}
                    identityValue={businessUnit.registrationNumber}
                  />
                </Form.Field>
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  autoComplete="off"
                  label={translateModels(t, 'referenceCode', 'businessUnit')}
                  name="referenceCode"
                  onChange={this.handleInputChange}
                  value={businessUnit.referenceCode}
                  ref={this.validation.referenceCode}
                />
                <AppForm.Input
                  label={t('Email')}
                  validateLabel={t('Email')}
                  name="email"
                  value={businessUnit.email}
                  onChange={this.handleInputChange}
                  ref={this.validation.email}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Validate
                  validation={[required]}
                  ref={this.validation.phonePrefix}
                  onChange={this.handleInputChange}
                  validateLabel={t('Phone prefix')}
                >
                  <Form.Select
                    label={t('Phone prefix')}
                    name="phonePrefix"
                    value={businessUnit.phonePrefix}
                    search
                    options={INTL_CALL_CODES}
                  />
                </AppForm.Validate>
                <PhoneInput
                  label={t('Phone')}
                  validateLabel={t('Phone')}
                  type="tel"
                  name="phone"
                  value={businessUnit.phone}
                  onChange={this.handleInputChange}
                  prefix={businessUnit.phonePrefix}
                  ref={this.validation.phone}
                  validation={[phone]}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  autoComplete="off"
                  label={`${t('Address')} 1*`}
                  name="address1"
                  onChange={this.handleInputChange}
                  placeholder={`${t('Address')} 1*`}
                  ref={this.validation.address1}
                  validation={[required, {
                    validate: stringLength,
                    option: {
                      max: 256,
                    },
                  }]}
                  value={businessUnit.address1}
                  validateLabel={`${t('Address')} 1`}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  autoComplete="off"
                  label={`${t('Address')} 2`}
                  name="address2"
                  onChange={this.handleInputChange}
                  placeholder={`${t('Address')} 2`}
                  value={businessUnit.address2}
                  ref={this.validation.address2}
                  validation={[{
                    validate: stringLength,
                    option: {
                      max: 256,
                    },
                  }]}
                  validateLabel={`${t('Address')} 2`}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  autoComplete="off"
                  label={`${t('Postcode')}*`}
                  name="postcode"
                  onChange={this.handleInputChange}
                  placeholder={`${t('Postcode')}*`}
                  value={businessUnit.postcode}
                  ref={this.validation.postcode}
                  validation={[required]}
                  validateLabel={t('Postcode')}
                />
                <AppForm.Input
                  autoComplete="off"
                  label={`${t('City')}*`}
                  name="city"
                  onChange={this.handleInputChange}
                  placeholder={`${t('City')}*`}
                  value={businessUnit.city}
                  ref={this.validation.city}
                  validation={[required]}
                  validateLabel={t('City')}
                />
              </Form.Group>
              <Form.Group widths="equal">
                { businessUnit.country.toUpperCase() === 'my'.toUpperCase() ?
                  <AppForm.Validate
                    validation={[required]}
                    ref={this.validation.state}
                    onChange={this.handleInputChange}
                    validateLabel={t('State')}
                  >
                    <Form.Select
                      fluid
                      label={`${t('State')}*`}
                      name="state"
                      options={STATES}
                      placeholder={`${t('Select State')}*`}
                      search
                      selectOnBlur={false}
                      value={businessUnit.state}
                    />
                  </AppForm.Validate>
                  :
                  <AppForm.Input
                    autoComplete="off"
                    fluid
                    label={`${t('State')}*`}
                    name="state"
                    onChange={this.handleInputChange}
                    placeholder={`${t('State')}*`}
                    ref={this.validation.state}
                    validation={[required]}
                    value={businessUnit.state}
                    validateLabel={t('State')}
                  />
                }
                <AppForm.Validate
                  validation={[required]}
                  ref={this.validation.country}
                  onChange={(e, data) => {
                    // Reset state input field
                    businessUnit.state = '';
                    this.setState({ businessUnit });
                    this.handleInputChange(e, data);
                  }}
                  validateLabel={t('Country')}
                >
                  <Form.Select
                    autoComplete="off"
                    fluid
                    label={`${t('Country')}*`}
                    name="country"
                    options={SELECT_COUNTRIES}
                    placeholder={`${t('Select Country')}*`}
                    search
                    selectOnBlur={false}
                    value={businessUnit.country}
                  />
                </AppForm.Validate>
              </Form.Group>
              <Form.Group widths="equal">
                <AppForm.Input
                  autoComplete="off"
                  label={translateModels(t, 'gps', 'businessUnit')}
                  name="gps"
                  onChange={this.handleInputChange}
                  placeholder={translateModels(t, 'gps', 'businessUnit')}
                />
                <Form.Select
                  autoComplete="off"
                  label={t('Access')}
                  name="statusID"
                  options={BU_STATUS_FILTER.map(status => ({
                    ...status,
                    text: t(status.text),
                  }))}
                  placeholder={t('Select Access')}
                  selectOnBlur={false}
                  value={businessUnit.statusID}
                  onChange={this.handleInputChange}
                />
              </Form.Group>
              <Form.Group>
                <Form.Checkbox
                  label={t('View all projects')}
                  name="viewAllProjects"
                  checked={businessUnit.viewAllProjects}
                  onChange={this.handleOnCheck}
                />
              </Form.Group>
              <Form.Group>
                <Form.Checkbox
                  label={t('View archived projects')}
                  name="viewArchivedProjects"
                  checked={businessUnit.viewArchivedProjects}
                  onChange={this.handleOnCheck}
                  disabled={!businessUnit.viewAllProjects}
                />
              </Form.Group>
              <Form.Group>
                <Form.Checkbox
                  label={t('View draft projects')}
                  name="viewDraftProjects"
                  checked={businessUnit.viewDraftProjects}
                  onChange={this.handleOnCheck}
                  disabled={!businessUnit.viewAllProjects}
                />
              </Form.Group>
              <Form.Group>
                <Form.Checkbox
                  label={t('View ready projects')}
                  name="viewReadyProjects"
                  checked={businessUnit.viewReadyProjects}
                  onChange={this.handleOnCheck}
                  disabled={!businessUnit.viewAllProjects}
                />
              </Form.Group>
            </Grid.Column>
          </Grid.Row>
          {
            businessUnit && !businessUnit.viewAllProjects ? (
              <Grid.Row>
                <Grid.Column>
                  <Divider horizontal>Projects</Divider>
                  {
                    businessUnit.projects &&
                    businessUnit.projects.length > 0 ? (
                      <Table unstackable>
                        <Table.Body>
                          {
                            businessUnit.projects.map((project, idx) => (
                              <Table.Row key={idx}>
                                <Table.Cell>
                                  <ListItem
                                    item={project}
                                    header={item => item.name}
                                    subheader={item => item.status.name}
                                    rightComponent={
                                      <div>
                                        <Button
                                          type="button"
                                          compact
                                          negative
                                          size="mini"
                                          icon="remove"
                                          onClick={() => this.handleRemoveProject(idx)}
                                        />
                                      </div>
                                    }
                                  />
                                </Table.Cell>
                              </Table.Row>
                            ))
                          }
                        </Table.Body>
                      </Table>
                    ) : (
                      <Segment textAlign="center">
                        {
                          this.state.loading ?
                            <Loader active inline="centered">Loading</Loader>
                          : 'No Business Unit found.'
                        }
                      </Segment>
                    )
                  }
                </Grid.Column>
              </Grid.Row>
            ) : null
          }
          {
            businessUnit && !businessUnit.viewAllProjects ? (
              <Grid.Row>
                <Grid.Column textAlign="center">
                  <Button
                    content="Add project"
                    onClick={this.handleShowAddProject}
                  />
                </Grid.Column>
              </Grid.Row>
            ) : null
          }
        </Grid>
      </AppForm>
    );
  }
}

export default withTranslation([
  ...namespaces,
  TRANSLATION_FILES.models.businessUnit,
], { withRef: true })(BusinessUnitForm);



// WEBPACK FOOTER //
// ./src/containers/BusinessUnits/Form.jsx